import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, WS_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SocialCtx = createContext(null);

export const SocialProvider = ({ children }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [threads, setThreads] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [blocks, setBlocks] = useState([]);
  const [mutes, setMutes] = useState([]);
  const wsRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timer: null });
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);
  const [dmEvent, setDmEvent] = useState(null); // last incoming dm signal

  const refreshFriends = useCallback(async () => {
    if (!user) return;
    try {
      const [fRes, rRes, tRes, bRes, mRes] = await Promise.all([
        api.get("/friends"), api.get("/friends/requests"),
        api.get("/dm/threads"), api.get("/users/blocks"), api.get("/users/mutes"),
      ]);
      setFriends(fRes.data);
      setRequests(rRes.data);
      setThreads(tRes.data.threads);
      setTotalUnread(tRes.data.total_unread);
      setBlocks(bRes.data);
      setMutes(mRes.data);
    } catch { /* noop */ }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setFriends([]); setRequests({ incoming: [], outgoing: [] }); setThreads([]); setTotalUnread(0); setBlocks([]); setMutes([]);
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } wsRef.current = null; }
      if (reconnectRef.current.timer) { clearTimeout(reconnectRef.current.timer); reconnectRef.current.timer = null; }
      return;
    }
    refreshFriends();

    let cancelled = false;
    let keepAlive = null;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem("sc_token");
      if (!token) return;
      const sock = new WebSocket(`${WS_BASE}/api/dm-ws?token=${encodeURIComponent(token)}`);
      wsRef.current = sock;

      sock.onopen = () => {
        reconnectRef.current.attempts = 0;
      };

      sock.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "dm") {
            setDmEvent(msg.message);
            refreshFriends();
            const m = msg.message;
            // Only notify for INCOMING DMs (not our own outgoing echoes).
            const isIncoming = m.to_user_id === user.id;
            if (isIncoming) {
              const loc = locationRef.current || {};
              const onThisThread = loc.pathname === "/messages" && (loc.search || "").includes(`to=${m.from_username}`);
              if (!onThisThread) {
                toast(m.from_username, {
                  description: (m.text || "").slice(0, 140),
                  action: {
                    label: "Reply",
                    onClick: () => nav(`/messages?to=${encodeURIComponent(m.from_username)}`),
                  },
                });
              }
            }
          } else if (msg.type === "friend_request") {
            refreshFriends();
            toast(`New friend request`, {
              description: `${msg.from} wants to be friends`,
              action: {
                label: "Open",
                onClick: () => nav(`/messages`),
              },
            });
          } else if (msg.type === "friend_accepted") {
            refreshFriends();
            toast(`Friend request accepted`, {
              description: `${msg.username} is now your friend`,
            });
          } else if (msg.type === "lfg_application") {
            // Someone applied/offered to our LFG post — notify with a
            // labeled toast the user can click straight to their inbox.
            toast(`🎮 LFG Application`, {
              description: `${msg.from} wants to join "${msg.post_title || "your post"}"`,
              duration: 8000,
              action: {
                label: "Open",
                onClick: () => nav(`/messages?to=${encodeURIComponent(msg.from)}`),
              },
            });
          } else if (msg.type === "lfg_response") {
            // Our application was accepted or declined — big deal → toast.
            const accepted = msg.status === "accepted";
            const from = msg.from || "The host";
            const title = msg.post_title || "the post";
            (accepted ? toast.success : toast)(
              `🎮 LFG Response — ${accepted ? "Accepted" : "Declined"}`,
              {
                description: accepted
                  ? `${from} accepted you for "${title}". Message them to coordinate.`
                  : `${from} passed on your application for "${title}".`,
                duration: 9000,
                action: from ? {
                  label: "Message",
                  onClick: () => nav(`/messages?to=${encodeURIComponent(from)}`),
                } : undefined,
              }
            );
          } else if (msg.type === "game_invite") {
            // Real-time game-room invitation from another user.
            const from = msg.from || "someone";
            const slug = msg.slug || "";
            const rid  = msg.room_id;
            toast(`🎮 Game invite`, {
              description: `${from} wants to play with you`,
              duration: 12000,
              action: rid ? {
                label: "Join",
                onClick: () => nav(`/games?join_room=${encodeURIComponent(rid)}&slug=${encodeURIComponent(slug)}`),
              } : undefined,
            });
          }
        } catch { /* noop */ }
      };

      sock.onclose = () => {
        if (cancelled) return;
        // Exponential backoff reconnect: 1s, 2s, 4s ... capped at 15s
        const attempts = Math.min(reconnectRef.current.attempts + 1, 6);
        reconnectRef.current.attempts = attempts;
        const delay = Math.min(1000 * Math.pow(2, attempts - 1), 15000);
        reconnectRef.current.timer = setTimeout(connect, delay);
      };

      sock.onerror = () => {
        try { sock.close(); } catch { /* noop */ }
      };
    };

    connect();
    // keep-alive
    keepAlive = setInterval(() => {
      try {
        if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send("ping");
      } catch { /* noop */ }
    }, 30000);

    return () => {
      cancelled = true;
      if (keepAlive) clearInterval(keepAlive);
      if (reconnectRef.current.timer) { clearTimeout(reconnectRef.current.timer); reconnectRef.current.timer = null; }
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } wsRef.current = null; }
    };
  }, [user, refreshFriends, nav]);

  const isBlocked = (username) => blocks.some(b => b.blocked_username === username);
  const isMuted = (username) => mutes.some(m => m.muted_username === username);

  const addFriend = async (username) => (await api.post("/friends/request", { username })).data;
  const acceptRequest = async (id) => { await api.post(`/friends/accept/${id}`); refreshFriends(); };
  const declineRequest = async (id) => { await api.post(`/friends/decline/${id}`); refreshFriends(); };
  const removeFriend = async (username) => { await api.delete(`/friends/${username}`); refreshFriends(); };
  const blockUser = async (username) => { await api.post(`/users/${username}/block`); refreshFriends(); };
  const unblockUser = async (username) => { await api.delete(`/users/${username}/block`); refreshFriends(); };
  const muteUser = async (username) => { await api.post(`/users/${username}/mute`); refreshFriends(); };
  const unmuteUser = async (username) => { await api.delete(`/users/${username}/mute`); refreshFriends(); };

  const value = useMemo(() => ({
    friends, requests, threads, totalUnread, blocks, mutes, dmEvent,
    refreshFriends, addFriend, acceptRequest, declineRequest, removeFriend,
    blockUser, unblockUser, muteUser, unmuteUser, isBlocked, isMuted,
  }), [friends, requests, threads, totalUnread, blocks, mutes, dmEvent, refreshFriends]);

  return <SocialCtx.Provider value={value}>{children}</SocialCtx.Provider>;
};

export const useSocial = () => useContext(SocialCtx) || {
  friends: [], requests: { incoming: [], outgoing: [] }, threads: [], totalUnread: 0,
  blocks: [], mutes: [], dmEvent: null, refreshFriends: () => {},
  addFriend: async () => {}, acceptRequest: async () => {}, declineRequest: async () => {}, removeFriend: async () => {},
  blockUser: async () => {}, unblockUser: async () => {}, muteUser: async () => {}, unmuteUser: async () => {},
  isBlocked: () => false, isMuted: () => false,
};
